/**
 * Block type registry — each block type declares:
 *   - label       Human name shown in the "Add block" picker
 *   - icon        lucide-react icon name
 *   - category    grouping in the picker
 *   - defaults    initial data when created
 *   - fields      list of typed fields for the editor
 *
 * Field types supported by the editor:
 *   text        single-line text
 *   textarea    multi-line text
 *   richtext    HTML (tiptap)
 *   image       image URL + preview
 *   link        { label, href }
 *   repeater    array of sub-forms (fields = [...])
 *   list        array of strings
 */

export const BLOCK_CATEGORIES = ["Hero", "Content", "Layout", "Media", "Utility"];

export const BLOCK_TYPES = {
  hero: {
    label: "Hero", icon: "flag", category: "Hero",
    defaults: {
      overline: "", headline: "New headline", subheadline: "",
      image: "", trust_line: "",
      primary_cta: { label: "Primary", href: "/register" },
      secondary_cta: { label: "Secondary", href: "/modules" },
    },
    fields: [
      { key: "overline", type: "text", label: "Overline (small caps)" },
      { key: "headline", type: "text", label: "Headline", required: true },
      { key: "subheadline", type: "textarea", label: "Subheadline" },
      { key: "image", type: "image", label: "Hero image URL" },
      { key: "trust_line", type: "text", label: "Trust line (below CTAs)" },
      { key: "primary_cta", type: "link", label: "Primary CTA" },
      { key: "secondary_cta", type: "link", label: "Secondary CTA" },
    ],
  },
  modules_hero: {
    label: "Modules Hero",
    icon: "blocks",
    category: "Hero",

    defaults: {
      badge: "ANALYSIS MODULES",
      title: "Production-Grade Pipelines.",
      description:
        "Every analysis pipeline is integrated into the SQIT.ai™ platform, version-controlled, and designed to deliver consistent, reproducible, and reliable results."
    },

    fields: [
      {
        key: "badge",
        type: "text",
        label: "Badge"
      },
      {
        key: "title",
        type: "text",
        label: "Title"
      },
      {
        key: "description",
        type: "textarea",
        label: "Description"
      }
    ]
  },
  contact_hero: {
    label: "Contact Hero",
    icon: "phone-call",
    category: "Hero",

    defaults: {
      badge: "CONTACT",
      title: "Let's talk about your project.",
      description:
        "Whether you're a student with a single dataset or a pharma team with an ongoing pipeline, we'd love to hear from you."
    },

    fields: [
      { key: "badge", type: "text", label: "Badge" },
      { key: "title", type: "text", label: "Title" },
      { key: "description", type: "textarea", label: "Description" }
    ]
  },
  contact_details: {
    label: "Contact Details",
    icon: "map-pinned",
    category: "Layout",

    defaults: {
      items: [
        {
          icon: "headset",
          title: "Support",
          line1: "sqit@bionivid.com",
          line2: "",
          line3: "",
          link1: "mailto:sqit@bionivid.com",
          link2: ""
        }
      ]
    },

    fields: [
      {
        key: "items",
        type: "repeater",
        label: "Contact Cards",
        itemLabel: item => item.title || "Card",
        fields: [
          { key: "icon", type: "text", label: "Lucide Icon" },
          { key: "title", type: "text", label: "Title" },
          { key: "line1", type: "text", label: "Line 1" },
          { key: "line2", type: "text", label: "Line 2" },
          { key: "line3", type: "text", label: "Line 3" },
          { key: "link1", type: "text", label: "Link 1" },
          { key: "link2", type: "text", label: "Link 2" },
          { key: "link3", type: "text", label: "Link 3" }
        ]
      }
    ]
  },
  contact_form: {
    label: "Contact Form",
    icon: "send",
    category: "Layout",

    defaults: {
      title: "Send us a Message",
      description: "Fill out the form below and we'll get back to you shortly.",
      button_text: "Send message",
      success_title: "Message received",
      success_message: "Thanks — we'll get back to you shortly."
    },

    fields: [
      { key: "title", type: "text", label: "Form Title" },
      { key: "description", type: "textarea", label: "Description" },
      { key: "button_text", type: "text", label: "Button Text" },
      { key: "success_title", type: "text", label: "Success Title" },
      { key: "success_message", type: "textarea", label: "Success Message" }
    ]
  },

  richtext: {
    label: "Rich Text", icon: "type", category: "Content",
    defaults: { heading: "", html: "<p>Write your content here…</p>" },
    fields: [
      { key: "heading", type: "text", label: "Heading (optional)" },
      { key: "html", type: "richtext", label: "Body" },
    ],
  },
  text: {
    label: "Plain Text", icon: "align-left", category: "Content",
    defaults: { heading: "", body: "" },
    fields: [
      { key: "heading", type: "text", label: "Heading (optional)" },
      { key: "body", type: "textarea", label: "Body" },
    ],
  },
  image: {
    label: "Image", icon: "image", category: "Media",
    defaults: { src: "", alt: "", caption: "" },
    fields: [
      { key: "src", type: "image", label: "Image URL", required: true },
      { key: "alt", type: "text", label: "Alt text (accessibility)" },
      { key: "caption", type: "text", label: "Caption" },
    ],
  },
  gallery: {
    label: "Image Gallery", icon: "images", category: "Media",
    defaults: { heading: "", items: [] },
    fields: [
      { key: "heading", type: "text", label: "Heading (optional)" },
      {
        key: "items", type: "repeater", label: "Images",
        itemLabel: (it) => it.alt || it.src || "Image",
        fields: [
          { key: "src", type: "image", label: "Image URL" },
          { key: "alt", type: "text", label: "Alt text" },
          { key: "caption", type: "text", label: "Caption" },
        ],
      },
    ],
  },
  "feature-list": {
    label: "Feature List (4 columns)", icon: "grid-3x3", category: "Layout",
    defaults: {
      overline: "", heading: "Section heading", layout: "standard",
      features: [
        { icon: "cpu", title: "Feature", text: "Short description" },
      ],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading", required: true },
      { key: "layout", type: "select", label: "Layout", options: ["standard", "compact"] },
      {
        key: "features", type: "repeater", label: "Features",
        itemLabel: (f) => f.title || "Feature",
        fields: [
          { key: "icon", type: "text", label: "Icon (lucide name)", hint: "e.g. cpu, shield-check, wallet, clock" },
          { key: "title", type: "text", label: "Title" },
          { key: "text", type: "textarea", label: "Description" },
        ],
      },
    ],
  },
  cards: {
    label: "Card Grid (3 columns)", icon: "layout-grid", category: "Layout",
    defaults: {
      overline: "", heading: "Section heading", layout: "standard",
      cards: [{ icon: "sparkles", title: "Card", text: "Card description" }],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading", required: true },
      { key: "layout", type: "select", label: "Layout", options: ["standard", "compact"] },
      {
        key: "cards", type: "repeater", label: "Cards",
        itemLabel: (c) => c.title || "Card",
        fields: [
          { key: "icon", type: "text", label: "Icon (lucide name)" },
          { key: "title", type: "text", label: "Title" },
          { key: "text", type: "textarea", label: "Description" },
        ],
      },
    ],
  },
  ecosystem_overview: {
    label: "Bioinformatics Ecosystem",
    icon: "layers-3",
    category: "Layout",

    defaults: {
      badge: "BIOINFORMATICS ECOSYSTEM",
      title: "Everything You Need for Bioinformatics",
      subtitle: "Software • Cloud Platform • Omics Solutions • Expert Services",
      description:
        "Bionivid offers a complete bioinformatics ecosystem for research laboratories, universities, hospitals, biotechnology companies, and clinical organizations. Our integrated solutions combine enterprise software, cloud-based analysis, Omics platforms, and expert bioinformatics services to support every stage of genomic and multi-omics research.",

      highlights: [
        "SQIT Software",
        "SQIT SaaS Platform",
        "Omics Station",
        "Bioinformatics Analysis Services"
      ],

      images: []
    },

    fields: [
      { key: "badge", type: "text", label: "Badge" },
      { key: "title", type: "text", label: "Title" },
      { key: "subtitle", type: "textarea", label: "Subtitle" },
      { key: "description", type: "richtext", label: "Description" },

      {
        key: "highlights",
        type: "list",
        label: "Highlights"
      },

      {
        key: "images",
        type: "repeater",
        label: "Images",
        itemLabel: item => item.caption || "Image",
        fields: [
          { key: "image", type: "image", label: "Image" },
          { key: "caption", type: "text", label: "Caption" }
        ]
      }
    ]
  },
  workflow: {
    label: "Workflow (numbered steps)", icon: "list-ordered", category: "Layout",
    defaults: {
      overline: "", heading: "How it works",
      steps: [{ n: "01", title: "Step", text: "Description" }],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading" },
      {
        key: "steps", type: "repeater", label: "Steps",
        itemLabel: (s) => `${s.n || ""} ${s.title || ""}`.trim() || "Step",
        fields: [
          { key: "n", type: "text", label: "Number (e.g. 01)" },
          { key: "title", type: "text", label: "Title" },
          { key: "text", type: "textarea", label: "Description" },
        ],
      },
    ],
  },
  testimonials: {
    label: "Testimonials", icon: "quote", category: "Content",
    defaults: {
      overline: "", heading: "Testimonials",
      quotes: [{ quote: "Great product.", author: "Name", role: "Title" }],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading" },
      {
        key: "quotes", type: "repeater", label: "Quotes",
        itemLabel: (q) => q.author || "Quote",
        fields: [
          { key: "quote", type: "textarea", label: "Quote" },
          { key: "author", type: "text", label: "Author" },
          { key: "role", type: "text", label: "Role / Company" },
        ],
      },
    ],
  },
  faq: {
    label: "FAQ (accordion)", icon: "help-circle", category: "Content",
    defaults: {
      overline: "", heading: "Frequently asked questions",
      items: [{ question: "Question?", answer: "Answer." }],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading" },
      {
        key: "items", type: "repeater", label: "Questions",
        itemLabel: (it) => it.question || "Question",
        fields: [
          { key: "question", type: "text", label: "Question" },
          { key: "answer", type: "richtext", label: "Answer" },
        ],
      },
    ],
  },
  stats: {
    label: "Stats", icon: "bar-chart-3", category: "Layout",
    defaults: {
      overline: "", heading: "By the numbers",
      items: [{ value: "10K+", label: "Samples processed" }],
    },
    fields: [
      { key: "overline", type: "text", label: "Overline" },
      { key: "heading", type: "text", label: "Heading (optional)" },
      {
        key: "items", type: "repeater", label: "Stats",
        itemLabel: (i) => `${i.value || ""} ${i.label || ""}`.trim() || "Stat",
        fields: [
          { key: "value", type: "text", label: "Value" },
          { key: "label", type: "text", label: "Label" },
        ],
      },
    ],
  },
  cta: {
    label: "Call-to-action banner", icon: "megaphone", category: "Utility",
    defaults: {
      heading: "Ready?", subheading: "",
      primary_cta: { label: "Primary", href: "/register" },
      secondary_cta: { label: "Secondary", href: "/contact" },
    },
    fields: [
      { key: "heading", type: "text", label: "Heading", required: true },
      { key: "subheading", type: "textarea", label: "Subheading" },
      { key: "primary_cta", type: "link", label: "Primary CTA" },
      { key: "secondary_cta", type: "link", label: "Secondary CTA" },
    ],
  },
  "button-group": {
    label: "Buttons", icon: "square-mouse-pointer", category: "Utility",
    defaults: {
      heading: "",
      buttons: [{ label: "Primary", href: "/register", style: "primary" }],
    },
    fields: [
      { key: "heading", type: "text", label: "Heading (optional)" },
      {
        key: "buttons", type: "repeater", label: "Buttons",
        itemLabel: (b) => b.label || "Button",
        fields: [
          { key: "label", type: "text", label: "Label" },
          { key: "href", type: "text", label: "URL" },
          { key: "style", type: "select", label: "Style", options: ["primary", "secondary", "outline"] },
        ],
      },
    ],
  },
  "link-list": {
    label: "Link List", icon: "link", category: "Utility",
    defaults: { heading: "Links", links: [{ label: "Link", href: "#" }] },
    fields: [
      { key: "heading", type: "text", label: "Heading" },
      {
        key: "links", type: "repeater", label: "Links",
        itemLabel: (l) => l.label || "Link",
        fields: [
          { key: "label", type: "text", label: "Label" },
          { key: "href", type: "text", label: "URL" },
        ],
      },
    ],
  },
  list: {
    label: "Bulleted List", icon: "list", category: "Content",
    defaults: { heading: "", items: ["First item"] },
    fields: [
      { key: "heading", type: "text", label: "Heading (optional)" },
      { key: "items", type: "list", label: "List items" },
    ],
  },
  hero_v2: {
    label: "Homepage Hero",
    icon: "flag",
    category: "Hero",

    defaults: {
      badge: "",
      title: "Enterprise Bioinformatics Platform",
      subtitle: "",
      description: "",
      image: "",
      primary_cta: {
        label: "Get Started",
        href: "/register",
      },
      secondary_cta: {
        label: "Explore Modules",
        href: "/modules",
      },
    },

    fields: [
      {
        key: "badge",
        type: "text",
        label: "Badge",
      },
      {
        key: "title",
        type: "text",
        label: "Title",
        required: true,
      },
      {
        key: "subtitle",
        type: "textarea",
        label: "Subtitle",
      },
      {
        key: "description",
        type: "textarea",
        label: "Description",
      },
      {
        key: "image",
        type: "image",
        label: "Hero Image",
      },
      {
        key: "primary_cta",
        type: "link",
        label: "Primary Button",
      },
      {
        key: "secondary_cta",
        type: "link",
        label: "Secondary Button",
      },
    ],
  },
  stats_v2: {
    label: "Homepage Statistics",
    icon: "bar-chart-3",
    category: "Layout",

    defaults: {
      items: [
        { icon: "book-open", value: "50+", label: "Analysis Pipelines" },
        { icon: "chart-column", value: "10M+", label: "Samples Processed" },
        { icon: "building-2", value: "100+", label: "Organizations" },
        { icon: "workflow", value: "25+", label: "Automated Workflows" },
        { icon: "dna", value: "20+", label: "Omics Modules" },
        { icon: "monitor", value: "99.9%", label: "Platform Availability" },
      ],
    },

    fields: [
      {
        key: "items",
        type: "repeater",
        label: "Statistics",
        itemLabel: (item) => item.label || "Statistic",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon",
          },
          {
            key: "value",
            type: "text",
            label: "Value",
          },
          {
            key: "label",
            type: "text",
            label: "Label",
          },
        ],
      },
    ],
  },
  omics_station: {
    label: "Omics Station",
    icon: "cpu",
    category: "Layout",

    defaults: {
      badge: "",
      title: "Omics Station",
      subtitle: "",
      description: "",
      image: "",

      features: [
        {
          icon: "cpu",
          title: "Feature",
          text: "Description"
        }
      ],

      stats: [
        {
          icon: "book-open",
          value: "250+",
          label: "Publications",
        },
      ],

      button: {
        label: "Explore Omics Station",
        href: "/documentation"
      }
    },

    fields: [
      {
        key: "badge",
        type: "text",
        label: "Badge"
      },
      {
        key: "title",
        type: "text",
        label: "Title"
      },
      {
        key: "subtitle",
        type: "textarea",
        label: "Subtitle"
      },
      {
        key: "description",
        type: "textarea",
        label: "Description"
      },
      {
        key: "image",
        type: "image",
        label: "Image"
      },
      {
        key: "features",
        type: "repeater",
        label: "Features",
        itemLabel: item => item.title || "Feature",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "text",
            type: "textarea",
            label: "Description"
          }
        ]
      },
      {
        key: "stats",
        type: "repeater",
        label: "Statistics",
        itemLabel: (item) => item.label || "Statistic",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon",
          },
          {
            key: "value",
            type: "text",
            label: "Value",
          },
          {
            key: "label",
            type: "text",
            label: "Label",
          },
        ],
      },
      {
        key: "button",
        type: "link",
        label: "Button"
      }
    ]
  },
  sqit_software: {
    label: "SQIT.ai™ Software",
    icon: "monitor",
    category: "Layout",

    defaults: {
      badge: "",
      title: "SQIT.ai™ Software",
      subtitle: "",
      description: "",
      images: [],

      features: [
        {
          icon: "layout-dashboard",
          title: "Feature",
          text: "Description"
        }
      ],

      stats: [
        {
          icon: "book-open",
          value: "250+",
          label: "Publications",
        },
      ],

      button: {
        label: "Explore SQIT.ai™ Software",
        href: "/documentation"
      }
    },

    fields: [
      {
        key: "badge",
        type: "text",
        label: "Badge"
      },
      {
        key: "title",
        type: "text",
        label: "Title"
      },
      {
        key: "subtitle",
        type: "textarea",
        label: "Subtitle"
      },
      {
        key: "description",
        type: "richtext",
        label: "Description"
      },
      {
        key: "images",
        type: "repeater",
        label: "Images",
        itemLabel: (item) => item.caption || "Images",
        fields: [
          {
            key: "image",
            type: "image",
            label: "Image",
          },
          {
            key: "caption",
            type: "text",
            label: "Caption",
          },
        ],
      },
      {
        key: "features",
        type: "repeater",
        label: "Features",
        itemLabel: item => item.title || "Feature",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "text",
            type: "textarea",
            label: "Description"
          }
        ]
      },
      {
        key: "stats",
        type: "repeater",
        label: "Statistics",
        itemLabel: (item) => item.label || "Statistic",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon",
          },
          {
            key: "value",
            type: "text",
            label: "Value",
          },
          {
            key: "label",
            type: "text",
            label: "Label",
          },
        ],
      },
      {
        key: "button",
        type: "link",
        label: "Button"
      }
    ]
  },
  sqit_saas: {
    label: "SQIT.ai™ SaaS Platform",
    icon: "cloud",
    category: "Layout",

    defaults: {
      badge: "",
      title: "SQIT.ai™ SaaS Platform",
      subtitle: "",
      description: "",
      image: "",

      features: [
        {
          icon: "cloud",
          title: "Feature",
          text: "Description"
        }
      ],

      button: {
        label: "Start Your Analysis",
        href: "/register"
      }
    },

    fields: [
      {
        key: "badge",
        type: "text",
        label: "Badge"
      },
      {
        key: "title",
        type: "text",
        label: "Title"
      },
      {
        key: "subtitle",
        type: "textarea",
        label: "Subtitle"
      },
      {
        key: "description",
        type: "richtext",
        label: "Description"
      },
      {
        key: "image",
        type: "image",
        label: "Image"
      },
      {
        key: "features",
        type: "repeater",
        label: "Features",
        itemLabel: item => item.title || "Feature",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "text",
            type: "textarea",
            label: "Description"
          }
        ]
      },
      {
        key: "button",
        type: "link",
        label: "Button"
      }
    ]
  },
  why_choose: {
    label: "Why Choose SQIT.ai™",
    icon: "sparkles",
    category: "Layout",

    defaults: {
      title: "Why Researchers Choose SQIT.ai™",

      cards: [
        {
          icon: "shield-check",
          title: "Feature",
          text: "Description"
        }
      ]
    },

    fields: [
      {
        key: "title",
        type: "text",
        label: "Section Title"
      },
      {
        key: "cards",
        type: "repeater",
        label: "Cards",
        itemLabel: (item) => item.title || "Card",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "text",
            type: "textarea",
            label: "Description"
          }
        ]
      }
    ]
  },
  supported_modules: {
    label: "Supported Analysis Modules",
    icon: "blocks",
    category: "Layout",

    defaults: {
      title: "Supported Analysis Modules",

      modules: [
        {
          icon: "dna",
          title: "Module",
          tagline: "Short tagline",
          description: "Detailed description",
          sample_report: "",
          show_sample_report: false
        }
      ]
    },

    fields: [
      {
        key: "title",
        type: "text",
        label: "Section Title"
      },
      {
        key: "modules",
        type: "repeater",
        label: "Modules",
        itemLabel: item => item.title || "Module",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Module Name"
          },
          {
            key: "tagline",
            type: "textarea",
            label: "Tagline"
          },
          {
            key: "description",
            type: "textarea",
            label: "Description"
          },
          {
            key: "sample_report",
            type: "text",
            label: "Sample Report Slug"
          },
          {
            key: "show_sample_report",
            type: "checkbox",
            label: "Show View Report Button"
          }
        ]
      }
    ]
  },
  workflow_v2: {
    label: "Homepage Workflow",
    icon: "git-branch",
    category: "Layout",

    defaults: {
      title: "How SQIT.ai™ Works",

      steps: [
        {
          icon: "cloud-upload",
          number: "01",
          title: "Upload Data",
          text: "Description"
        }
      ]
    },

    fields: [
      {
        key: "title",
        type: "text",
        label: "Section Title"
      },
      {
        key: "steps",
        type: "repeater",
        label: "Workflow Steps",
        itemLabel: item => item.title || "Step",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "number",
            type: "text",
            label: "Step Number"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "text",
            type: "textarea",
            label: "Description"
          }
        ]
      }
    ]
  },
  infrastructure_info: {
    label: "Infrastructure & Support",
    icon: "server",
    category: "Layout",

    defaults: {
      cards: [
        {
          icon: "server",
          title: "Software Infrastructure",
          type: "list",
          image: "",
          items: ["Item 1"]
        }
      ]
    },

    fields: [
      {
        key: "cards",
        type: "repeater",
        label: "Cards",
        itemLabel: item => item.title || "Card",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "type",
            type: "select",
            options: [
              { label: "List", value: "list" },
              { label: "Table", value: "table" },
              { label: "Image", value: "image" }
            ]
          },
          {
            key: "image",
            type: "image",
            label: "Image"
          },
          {
            key: "items",
            type: "list",
            label: "Items"
          }
        ]
      }
    ]
  },
  info_cards: {
    label: "Information Cards",
    icon: "layout-grid",
    category: "Layout",

    defaults: {
      cards: [
        {
          icon: "file-text",
          title: "Card Title",
          description: "Description",
          button: {
            label: "Learn More",
            href: "/"
          },
          links: []
        }
      ]
    },

    fields: [
      {
        key: "cards",
        type: "repeater",
        label: "Cards",
        itemLabel: item => item.title || "Card",
        fields: [
          {
            key: "icon",
            type: "text",
            label: "Lucide Icon"
          },
          {
            key: "title",
            type: "text",
            label: "Title"
          },
          {
            key: "description",
            type: "textarea",
            label: "Description"
          },
          {
            key: "links",
            type: "list",
            label: "Resource Links"
          },
          {
            key: "button",
            type: "link",
            label: "Button"
          }
        ]
      }
    ]
  },
  cta_v2: {
    label: "Homepage CTA",
    icon: "megaphone",
    category: "Layout",

    defaults: {
      title: "Ready to Transform Your Bioinformatics Research?",
      description:
        "Discover how SQIT.ai™ can standardize, simplify, and scale your sequencing workflows—from a single experiment to enterprise-level genomics environments.",
      button: {
        label: "Try SQIT.ai™ Software",
        href: "/contact"
      }
    },

    fields: [
      {
        key: "title",
        type: "text",
        label: "Title"
      },
      {
        key: "description",
        type: "textarea",
        label: "Description"
      },
      {
        key: "button",
        type: "link",
        label: "Button"
      }
    ]
  },
};

export function makeBlock(type) {
  const spec = BLOCK_TYPES[type];
  if (!spec) throw new Error(`Unknown block type: ${type}`);
  // deep-clone defaults
  const data = JSON.parse(JSON.stringify(spec.defaults));
  return {
    key: `${type}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    data,
    order: 0,
  };
}

export function getBlockLabel(type) {
  return BLOCK_TYPES[type]?.label || type;
}
